import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

/**
 * JSON Schemas for the `Configuration` blob, one per LayerTypeCode.
 *
 * The Configuration column feeds the ArcGIS JS API layer constructor almost
 * verbatim, so the property sets below mirror the constructor properties of
 * the corresponding layer class — enriched with hover docs and enums.
 *
 * Validation philosophy: the TOP LEVEL is strict (`additionalProperties:
 * false`) so typos and misplaced keys get a warning squiggle, while NESTED
 * structures (renderers, popup templates, …) stay permissive because the Esri
 * formats are too deep to mirror exhaustively. Warnings never block saving.
 */

const ESRI_LAYER_DOCS_BASE_URL =
  "https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-"

/** Esri `blendMode` values, shared by every layer type. */
const BLEND_MODES = [
  "average",
  "color-burn",
  "color-dodge",
  "color",
  "darken",
  "destination-atop",
  "destination-in",
  "destination-out",
  "destination-over",
  "difference",
  "exclusion",
  "hard-light",
  "hue",
  "invert",
  "lighten",
  "lighter",
  "luminosity",
  "minus",
  "multiply",
  "normal",
  "overlay",
  "plus",
  "reflect",
  "saturation",
  "screen",
  "soft-light",
  "source-atop",
  "source-in",
  "source-out",
  "vivid-light",
  "xor",
]

/** Constructor properties every ArcGIS layer type accepts. */
const SHARED_LAYER_PROPERTIES: Record<string, JsonSchema> = {
  id: {
    type: "string",
    description:
      "Unique layer id within the map. Usually mirrors the FeatureCode.",
  },
  title: {
    type: "string",
    description: "Display title used in the layer list and legend.",
  },
  url: {
    type: "string",
    description: "Service endpoint URL for the layer.",
  },
  opacity: {
    type: "number",
    minimum: 0,
    maximum: 1,
    description: "Layer opacity between 0 (transparent) and 1 (opaque).",
  },
  visible: {
    type: "boolean",
    description: "Whether the layer is initially visible on the map.",
  },
  blendMode: {
    type: "string",
    enum: BLEND_MODES,
    description:
      "How the layer blends with the layers beneath it (Esri blendMode).",
  },
  effect: {
    description:
      'Esri layer effect, e.g. "drop-shadow(2px, 2px, 3px)" — a string or scale-dependent array.',
    type: ["string", "array"],
  },
  minScale: {
    type: "number",
    description:
      "Maximum zoom-OUT at which the layer is visible (0 = no limit). Larger number = further out.",
  },
  maxScale: {
    type: "number",
    description:
      "Maximum zoom-IN at which the layer is visible (0 = no limit).",
  },
  listMode: {
    type: "string",
    enum: ["show", "hide", "hide-children"],
    description: "How the layer appears in layer-list widgets.",
  },
  type: {
    type: "string",
    description:
      "ArcGIS layer type discriminator. Should agree with the LayerTypeCode of this layer.",
    enum: [
      "feature",
      "group",
      "map-image",
      "wms",
      "ogc-feature",
      "scene",
      "imagery",
      "tile",
      "vector-tile",
      "geojson",
      "csv",
    ],
  },
  layerType: {
    type: "string",
    description:
      "Legacy webmap-spec type discriminator (kept for older configs). Prefer `type`.",
  },
  portalItem: {
    type: "object",
    description:
      "Load the layer from a portal item instead of a direct service URL.",
    properties: {
      id: {
        type: "string",
        description: "Portal item id (32-char hex from AGOL).",
      },
      portal: {
        type: "object",
        properties: {
          url: {
            type: "string",
            description:
              "Portal base URL. Defaults to ArcGIS Online when omitted.",
          },
        },
      },
    },
  },
}

/** Popup template — permissive, but with docs for the common keys. */
const POPUP_TEMPLATE: JsonSchema = {
  type: "object",
  markdownDescription:
    "Popup shown when a feature is clicked. See [PopupTemplate](" +
    ESRI_LAYER_DOCS_BASE_URL.replace("esri-layers-", "esri-") +
    "PopupTemplate.html).",
  properties: {
    title: {
      type: "string",
      description: 'Popup title, supports field placeholders like "{Name}".',
    },
    content: {
      description:
        "Popup body: a string with placeholders, or an array of content elements (fields, text, media, …).",
      type: ["string", "array"],
    },
    fieldInfos: {
      type: "array",
      description: "Field formatting used by `fields` content elements.",
      items: {
        type: "object",
        properties: {
          fieldName: { type: "string" },
          label: { type: "string" },
          visible: { type: "boolean" },
          format: { type: "object" },
        },
      },
    },
    outFields: {
      type: "array",
      items: { type: "string" },
      description: 'Fields fetched for the popup. Use ["*"] for all.',
    },
    expressionInfos: { type: "array" },
    actions: { type: "array" },
    overwriteActions: { type: "boolean" },
  },
}

/** Renderer — enum on `type`, everything else permissive. */
const RENDERER: JsonSchema = {
  type: "object",
  markdownDescription:
    "How features are symbolized. See [renderers](" +
    ESRI_LAYER_DOCS_BASE_URL.replace("esri-layers-", "esri-renderers-") +
    "Renderer.html).",
  properties: {
    type: {
      type: "string",
      enum: [
        "simple",
        "unique-value",
        "class-breaks",
        "heatmap",
        "dot-density",
        "dictionary",
        "pie-chart",
      ],
      description: "Renderer kind.",
    },
    field: {
      type: "string",
      description: "Attribute field driving the renderer.",
    },
    symbol: { type: "object", description: "Symbol for simple renderers." },
    defaultSymbol: { type: "object" },
    defaultLabel: { type: "string" },
    uniqueValueInfos: {
      type: "array",
      description: "Value → symbol mapping for unique-value renderers.",
    },
    classBreakInfos: {
      type: "array",
      description: "Range → symbol mapping for class-breaks renderers.",
    },
    visualVariables: { type: "array" },
  },
}

const LABELING_INFO: JsonSchema = {
  type: "array",
  description: "Label classes (LabelClass[]) applied to the layer.",
  items: {
    type: "object",
    properties: {
      labelExpressionInfo: {
        type: "object",
        properties: {
          expression: {
            type: "string",
            description: 'Arcade expression, e.g. "$feature.Name".',
          },
        },
      },
      symbol: { type: "object" },
      minScale: { type: "number" },
      maxScale: { type: "number" },
      labelPlacement: { type: "string" },
      where: {
        type: "string",
        description: "SQL filter for this label class.",
      },
    },
  },
}

function makeConfigurationSchema(input: {
  docsPage: string
  properties: Record<string, JsonSchema>
}): JsonSchema {
  return {
    // Root may be null: the blob is optional in the database.
    type: ["object", "null"],
    markdownDescription:
      "ArcGIS layer constructor properties. See [" +
      input.docsPage +
      "](" +
      ESRI_LAYER_DOCS_BASE_URL +
      input.docsPage +
      ".html).",
    properties: { ...SHARED_LAYER_PROPERTIES, ...input.properties },
    // Strict top level: unknown keys get a (non-blocking) warning.
    additionalProperties: false,
  }
}

const FEATURE_CONFIGURATION = makeConfigurationSchema({
  docsPage: "FeatureLayer",
  properties: {
    layerId: {
      type: "number",
      description:
        "Sublayer index in the feature service (the /N suffix of the URL).",
    },
    renderer: RENDERER,
    popupTemplate: POPUP_TEMPLATE,
    popupEnabled: { type: "boolean" },
    labelsVisible: {
      type: "boolean",
      description: "Whether labels from labelingInfo are shown.",
    },
    labelingInfo: LABELING_INFO,
    definitionExpression: {
      type: "string",
      description: "SQL WHERE clause limiting which features are loaded.",
    },
    outFields: {
      type: "array",
      items: { type: "string" },
      description: 'Attribute fields to fetch. Use ["*"] for all fields.',
    },
    fields: {
      type: "array",
      description: "Override/define the field schema client-side.",
    },
    attributeTableTemplate: {
      type: "object",
      description: "Esri attribute-table template for the layer.",
    },
    queryable: {
      type: "boolean",
      description:
        "Note: not a FeatureLayer constructor property — verify it is actually consumed.",
    },
    orderBy: { type: "array", description: "Feature draw/query order." },
    featureReduction: {
      type: "object",
      description: "Clustering / binning configuration.",
    },
    elevationInfo: { type: "object" },
    timeInfo: { type: "object" },
    customParameters: {
      type: "object",
      description:
        "Extra query-string parameters sent with every request. For a Placepoint dataset layer (…/dataset/<id>/FeatureServer) this is how the generic service fields get their values: " +
        '{"enforceTileBatching": "true", "customParameters": "<JSON string>"} where the inner JSON maps categorySourceName / colormapSourceName / sizeSourceName / labelSourceName to a dataset column\'s sourceName (e.g. "Column_2"); renderer, labels and popups then reference category / colormap / size / label.',
      properties: {
        enforceTileBatching: { type: "string" },
        customParameters: {
          type: "string",
          description:
            "JSON document, serialised as a string, naming the dataset columns bound to the generic fields.",
        },
      },
    },
  },
})

const GROUP_CONFIGURATION = makeConfigurationSchema({
  docsPage: "GroupLayer",
  properties: {
    visibilityMode: {
      type: "string",
      enum: ["independent", "inherited", "exclusive"],
      description:
        "independent: children toggle freely · exclusive: only one child visible · inherited: children follow the group.",
    },
    layers: {
      type: "array",
      description:
        "Child layer configurations. Each child follows the same shape as a top-level Configuration for its type.",
      items: {
        type: "object",
        properties: {
          type: SHARED_LAYER_PROPERTIES.type,
          title: SHARED_LAYER_PROPERTIES.title,
          url: SHARED_LAYER_PROPERTIES.url,
        },
        // Children are full layer configs of any type — stay permissive.
        additionalProperties: true,
      },
    },
  },
})

const MAP_IMAGE_CONFIGURATION = makeConfigurationSchema({
  docsPage: "MapImageLayer",
  properties: {
    sublayers: {
      type: "array",
      description: "Sublayer overrides for the map service.",
      items: {
        type: "object",
        properties: {
          id: { type: "number", description: "Sublayer id in the service." },
          visible: { type: "boolean" },
          title: { type: "string" },
          definitionExpression: { type: "string" },
          popupTemplate: POPUP_TEMPLATE,
          renderer: RENDERER,
          labelingInfo: LABELING_INFO,
          labelsVisible: { type: "boolean" },
          minScale: { type: "number" },
          maxScale: { type: "number" },
          sublayers: { type: "array" },
        },
      },
    },
    visibleLayers: {
      type: "array",
      items: { type: "number" },
      description:
        "Legacy webmap-spec key listing visible sublayer ids. Prefer `sublayers` with `visible`.",
    },
    dpi: { type: "number" },
    imageFormat: { type: "string" },
    imageTransparency: { type: "boolean" },
  },
})

const WMS_CONFIGURATION = makeConfigurationSchema({
  docsPage: "WMSLayer",
  properties: {
    sublayers: {
      type: "array",
      description: "WMS sublayers to request.",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "WMS layer name as published by the service.",
          },
          title: {
            type: "string",
            description: "Display title in the legend.",
          },
          legendUrl: { type: "string" },
          legendEnabled: { type: "boolean" },
          queryable: { type: "boolean" },
          visible: { type: "boolean" },
          sublayers: { type: "array" },
        },
      },
    },
    featureInfoFormat: {
      type: "string",
      description:
        'MIME type used for GetFeatureInfo requests, e.g. "text/html".',
    },
    featureInfoUrl: {
      type: "string",
      description: "GetFeatureInfo endpoint (enables clickable popups).",
    },
    version: { type: "string", description: "WMS protocol version." },
    customParameters: {
      type: "object",
      description: "Extra query-string parameters appended to WMS requests.",
    },
    imageFormat: { type: "string" },
    imageTransparency: { type: "boolean" },
  },
})

const OGC_FEATURE_CONFIGURATION = makeConfigurationSchema({
  docsPage: "OGCFeatureLayer",
  properties: {
    collectionId: {
      type: "string",
      description: "Collection id within the OGC API Features service.",
    },
    renderer: RENDERER,
    popupTemplate: POPUP_TEMPLATE,
    // Legacy webmap-spec keys observed in existing configs:
    layerDefinition: {
      type: "object",
      description:
        "Legacy webmap-spec drawing info. Prefer `renderer` on the root.",
    },
    popupInfo: {
      type: "object",
      description: "Legacy webmap-spec popup info. Prefer `popupTemplate`.",
    },
    visibility: {
      type: "boolean",
      description: "Legacy webmap-spec key. Prefer `visible`.",
    },
    showLabels: { type: "boolean", description: "Legacy webmap-spec key." },
    disablePopup: {
      type: "boolean",
      description: "Legacy webmap-spec key. Prefer `popupEnabled: false`.",
    },
    definitionExpression: { type: "string" },
    labelingInfo: LABELING_INFO,
    labelsVisible: { type: "boolean" },
  },
})

const SCENE_CONFIGURATION = makeConfigurationSchema({
  docsPage: "SceneLayer",
  properties: {
    renderer: RENDERER,
    popupTemplate: POPUP_TEMPLATE,
    popupEnabled: { type: "boolean" },
    definitionExpression: { type: "string" },
    elevationInfo: { type: "object" },
    outFields: { type: "array", items: { type: "string" } },
  },
})

const IMAGERY_CONFIGURATION = makeConfigurationSchema({
  docsPage: "ImageryLayer",
  properties: {
    renderer: RENDERER,
    popupTemplate: POPUP_TEMPLATE,
    format: { type: "string" },
    bandIds: { type: "array", items: { type: "number" } },
    interpolation: { type: "string" },
    mosaicRule: { type: "object" },
    rasterFunction: { type: "object" },
  },
})

/**
 * Fallback for layer types without a curated schema: shared props only, and
 * unknown keys are allowed (no warning) since we don't know the full shape.
 */
const GENERIC_CONFIGURATION: JsonSchema = {
  type: ["object", "null"],
  description: "ArcGIS layer constructor properties.",
  properties: SHARED_LAYER_PROPERTIES,
  additionalProperties: true,
}

export const CONFIGURATION_SCHEMAS_BY_LAYER_TYPE: Record<string, JsonSchema> = {
  feature: FEATURE_CONFIGURATION,
  group: GROUP_CONFIGURATION,
  "map-image": MAP_IMAGE_CONFIGURATION,
  wms: WMS_CONFIGURATION,
  "ogc-feature": OGC_FEATURE_CONFIGURATION,
  scene: SCENE_CONFIGURATION,
  imagery: IMAGERY_CONFIGURATION,
}

export const GENERIC_CONFIGURATION_SCHEMA = GENERIC_CONFIGURATION
